import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import EditWeldNumberModal from "./EditWeldNumberModal.js";
import editIcon from "../../assets/edit.png";
import { statusCalculationMethodData360LiveEnum } from "../../constants/commonConstants.js";
import { aggridHelpers } from "../../helpers/common/ag-grid-helper.js";
import { logDataHelperFunctions } from "../../helpers/logdata-helper";
import { hasEditPermission } from "../../helpers/rabc-helper";
import { FETCH_LIVE_WELD_STATUS_SAGA } from "../../redux/data360LiveSlog/types";
import EnterpriseGridCommon from "../AgGrid/Ag-Grid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useTranslation } from "react-i18next";
import ErrorBanner from "../Common/ErrorBanner";
import CommonLoader from "../Loaders/loader";

const D360LiveSlogStatus = ({
  handleWeldNumberChange,
  handleDateFilterChange,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { weldStatusList, isWeldStatusLoading, weldParameterErrors } =
    useSelector((state) => state.data360LiveSlogReducer);
  const currentProject = useSelector(
    (state) => state?.data360LiveProjectReducer?.currentProject
  );

  const toggleProjectListState = useSelector(
    (state) => state?.switchThemeReducer?.toggleProjectList
  );

  const toggleProjectDetailsState = useSelector(
    (state) => state?.switchThemeReducer?.toggleProjectDetails
  );

  const { t } = useTranslation();
  const reducerList = useSelector((state) => state);

  const canEdit = hasEditPermission(reducerList?.auth?.userDetails);

  // Extract error messages from weldParameterErrors
  const getErrorMessages = () => {
    if (
      !weldParameterErrors?.errors ||
      weldParameterErrors.errors.length === 0
    ) {
      return [];
    }

    const errorMessages = [];
    weldParameterErrors.errors.forEach((error) => {
      if (error.parsedErrors?.errorMessages) {
        errorMessages.push(...error.parsedErrors.errorMessages);
      }
    });

    return errorMessages;
  };

  const errorMessages = getErrorMessages();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedWeld, setSelectedWeld] = useState(null);
  const [newWeldNumber, setNewWeldNumber] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  const handleEditClick = useCallback((weldNumber, event) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setSelectedWeld(weldNumber);
    setNewWeldNumber("");
    setAdminPassword("");
    setIsEditModalOpen(true);
  }, []);

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedWeld(null);
    setNewWeldNumber("");
    setAdminPassword("");
  };

  const handleModalConfirm = () => {
    setIsEditModalOpen(false);
    setSelectedWeld(null);
    setNewWeldNumber("");
    setAdminPassword("");

    if (currentProject?.project_id && currentProject?.project_name) {
      fetchData();
    }
  };

  const [columnDefs] = useState([
    {
      headerName: t("sLogData.slNo"),
      field: "sl_no",
      key: "sl_no",
      flex: 1,
      minWidth: 100,
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        maxNumConditions: 1,
        cellHeight: 30,
      },
      pivotComparator: (a, b) => b.localeCompare(a),
    },
    {
      headerName: t("sLogData.weldNumber"),
      field: "weldNumber",
      key: "weldNumber",
      flex: 1,
      minWidth: 100,
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        maxNumConditions: 1,
      },
      pivotComparator: (a, b) => b.localeCompare(a),
      cellRenderer: (params) => (
        <>
          {canEdit ? (
            <div className="weld-number-container">
              <span>{params.value}</span>
              <img
                src={editIcon}
                alt="Edit"
                style={{
                  cursor: "pointer",
                }}
                onClick={(e) => handleEditClick(params.value, e)}
                onMouseDown={(e) => e.stopPropagation()}
              />
            </div>
          ) : (
            <span>{params.value}</span>
          )}
        </>
      ),
    },
    {
      headerName: t("sLogData.status"),
      field: "status",
      key: "status",
      flex: 1,
      minWidth: 100,
      cellRenderer: (params) =>
        logDataHelperFunctions.getIconForStatus(params.value),
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellHeight: 40,
              cellRenderer: (params) => {
                const status = String(params.value);
                if (status === "(Select All)") {
                  return "(Select All)";
                }
                return logDataHelperFunctions.getIconForStatus(status);
              },
              valueFormatter: (params) => {
                const status = String(params.value);
                return status;
              },
            },
          },
        ],
        maxNumConditions: 1,
      },
      processCellForExport: (params) => {
        return params.value
          ? logDataHelperFunctions.getExportValueForStatus(
              params.value?.toLowerCase()
            )
          : "";
      },
      pivotComparator: (a, b) => b.localeCompare(a),
    },
    {
      headerName: "Weld Start Time",
      field: "weldStartTime",
      key: "weldStartTime",
      flex: 1,
      minWidth: 100,
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      cellRenderer: (params) => {
        return Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTimeLive(
              params.value,
              false,
              false,
              true
            )
          : "-";
      },
      processCellForExport: (params) => {
        return Number(params.value) > 0
          ? logDataHelperFunctions.formatWeldStartTimeLive(
              params.value,
              false,
              false,
              true
            )
          : "-";
      },
      pivotComparator: (a, b) => b.localeCompare(a),
      filterParams: {
        maxNumConditions: 1,
        filters: [
          {
            filter: "agDateColumnFilter",
            filterParams: {
              comparator: (filterLocalDateAtMidnight, cellValue) => {
                return aggridHelpers.dateFilter(
                  filterLocalDateAtMidnight,
                  cellValue
                );
              },
              maxNumConditions: 1,
              inRangeInclusive: true,
            },
          },
          {
            filter: "agSetColumnFilter",
            filterParams: {
              cellRenderer: (params) => {
                const value = String(params.value);
                if (value === "(Select All)") {
                  return "(Select All)";
                }
                return Number(params.value) > 0
                  ? logDataHelperFunctions.formatWeldStartTimeLive(
                      params.value,
                      true
                    )
                  : "-";
              },
              comparator: (filterLocalDateAtMidnight, cellValue) => {
                return aggridHelpers.dateFilter(
                  filterLocalDateAtMidnight,
                  cellValue
                );
              },
            },
          },
        ],
      },
    },
    {
      headerName: t("sLogData.weldTime"),
      field: "weld_time",
      key: "weld_time",
      flex: 1,
      minWidth: 100,
      cellRenderer: (params) => {
        const value = params.value;
        if (
          value === 0 ||
          value === "0" ||
          value === "0.00" ||
          value === null ||
          value === undefined
        ) {
          return "-";
        }
        return value;
      },
      processCellForExport: (params) => {
        const value = params.value;
        if (
          value === 0 ||
          value === "0" ||
          value === "0.00" ||
          value === null ||
          value === undefined
        ) {
          return "-";
        }
        return value;
      },
      pivot: true,
      floatingFilter: true,
      filter: "agMultiColumnFilter",
      filterParams: {
        maxNumConditions: 1,
      },
      pivotComparator: (a, b) => b.localeCompare(a),
    },
  ]);

  useEffect(() => {
    if (currentProject?.project_id && currentProject?.project_name) {
      fetchData();
    }
  }, [dispatch, currentProject]);

  const fetchData = () => {
    dispatch({
      type: FETCH_LIVE_WELD_STATUS_SAGA,
      payload: {
        project_id: currentProject.project_id,
        project_name: currentProject.project_name,
      },
    });
  };

  const onRowClicked = (params) => {
    const target = params.event?.target;
    if (target && (target.tagName === "IMG" || target.closest("img"))) {
      return;
    }

    if (params?.data?.weldNumber) {
      history.push(`/data360-live-views/${params.data.weldNumber}`);
    }
  };

  const handleFilterChanged = (filterModel) => {
    let filteredData = [...dataSource];

    // Apply each column filter
    Object.keys(filterModel).forEach(columnKey => {
      const filter = filterModel[columnKey];

      if (filter && filter.filterModels) {
        filter.filterModels.forEach(filterModelItem => {

          if (filterModelItem && filterModelItem.filterType === 'text' && filterModelItem.filter) {
            // Text filter
            const searchText = filterModelItem.filter.toLowerCase();
            filteredData = filteredData.filter(row => {
              const cellValue = row[columnKey];
              return cellValue && String(cellValue).toLowerCase().includes(searchText);
            });
          } else if (filterModelItem && filterModelItem.filterType === 'set' && filterModelItem.values) {
            // Set filter (checkbox)
            filteredData = filteredData.filter(row => {
              const cellValue = row[columnKey];
              // Handle both string and number comparisons
              const stringMatch = filterModelItem.values.includes(String(cellValue));
              const numberMatch = filterModelItem.values.includes(Number(cellValue));
              const reverseStringMatch = filterModelItem.values.some(val => String(val) === String(cellValue));
              const reverseNumberMatch = filterModelItem.values.some(val => Number(val) === Number(cellValue));

              return stringMatch || numberMatch || reverseStringMatch || reverseNumberMatch;
            });
          } else if (filterModelItem && filterModelItem.filterType === 'number') {
            // Number filter
            if (filterModelItem.type === 'equals') {
              filteredData = filteredData.filter(row => row[columnKey] == filterModelItem.filter);
            } else if (filterModelItem.type === 'greaterThan') {
              filteredData = filteredData.filter(row => Number(row[columnKey]) > Number(filterModelItem.filter));
            } else if (filterModelItem.type === 'lessThan') {
              filteredData = filteredData.filter(row => Number(row[columnKey]) < Number(filterModelItem.filter));
            }
          }
        });
      } else if (filter && filter.values) {
        // Direct set filter (checkbox) - values array directly on filter object
        filteredData = filteredData.filter(row => {
          const cellValue = row[columnKey];
          // Handle both string and number comparisons
          const stringMatch = filter.values.includes(String(cellValue));
          const numberMatch = filter.values.includes(Number(cellValue));
          const reverseStringMatch = filter.values.some(val => String(val) === String(cellValue));
          const reverseNumberMatch = filter.values.some(val => Number(val) === Number(cellValue));

          return stringMatch || numberMatch || reverseStringMatch || reverseNumberMatch;
        });
      }
    });

    const filteredWeldNumbers = filteredData.map(row => row.weldNumber).filter(Boolean);


    if (handleWeldNumberChange && filteredWeldNumbers.length > 0) {
      handleWeldNumberChange(filteredWeldNumbers);
    }

    // Handle date filter separately for weldStartTime
    if (filterModel.weldStartTime) {
      let fromDate = filterModel.weldStartTime?.filterModels?.[0]?.dateFrom;
      let toDate = filterModel.weldStartTime?.filterModels?.[0]?.dateTo;
      fromDate = fromDate ? new Date(fromDate).valueOf() : null;
      toDate = toDate ? new Date(toDate).valueOf() : null;
      if (handleDateFilterChange) {
        handleDateFilterChange(fromDate, toDate);
      }
    }
  };

  const processCellCallback = useCallback((params) => {
    return (
      params?.column?.colDef?.processCellForExport?.(params) ?? params.value
    );
  }, []);

  const getStatusCalculationMethod = (
    statusCalculationMethod,
    weldStatusRecord
  ) => {
    switch (statusCalculationMethod) {
      case statusCalculationMethodData360LiveEnum.Instantaneous:
        return weldStatusRecord.statusByInstantaneous;
      case statusCalculationMethodData360LiveEnum.AvgByPass:
        return weldStatusRecord.statusByAvgPass;
      case statusCalculationMethodData360LiveEnum.AvgByZone:
        return weldStatusRecord.statusByAvgZone;
      case statusCalculationMethodData360LiveEnum.AvgByTilt:
        return weldStatusRecord.statusByAvgTilt;
      default:
        return weldStatusRecord.statusByInstantaneous;
    }
  };

  const dataSource = weldStatusList
    .map((item, index) => ({
      key: index,
      weldNumber: item.weldNumber,
      weldStartTime: item.start_time,
      weld_time:
        item.weld_time === 0 ||
        item.weld_time === null ||
        item.weld_time === undefined
          ? 0
          : item.weld_time,
      status: getStatusCalculationMethod(
        currentProject.statusCalculationMethod,
        item
      ),
    }))
    .sort((a, b) => b.weldStartTime - a.weldStartTime)
    .map((item, index) => ({
      ...item,
      sl_no: index + 1,
    }));

  let heightValue;

  if (!toggleProjectDetailsState && toggleProjectListState) {
    heightValue = -295;
  } else if (toggleProjectDetailsState && !toggleProjectListState) {
    heightValue = -250;
  } else if (!toggleProjectDetailsState && !toggleProjectListState) {
    heightValue = -175;
  } else if (toggleProjectDetailsState && toggleProjectListState) {
    heightValue = -370;
  }

  return (
    <div
      className="ag-theme-alpine slog-grid-container"
      style={{ width: "100%" }}
    >
      <ErrorBanner errors={errorMessages} />
      {isWeldStatusLoading ? (
        <div className="slogdata-loader">
          <CommonLoader />
        </div>
      ) : (
        <EnterpriseGridCommon
          rows={dataSource}
          columnDefs={columnDefs}
          isLoading={isWeldStatusLoading}
          onRowClicked={onRowClicked}
          exportFileName="Live-Slog-status"
          handleFilterChanged={handleFilterChanged}
          processCellCallback={processCellCallback}
          heightValue={heightValue}
          slogData={true}
        />
      )}
      <EditWeldNumberModal
        open={isEditModalOpen}
        weldNumber={selectedWeld}
        newWeldNumber={newWeldNumber}
        setNewWeldNumber={setNewWeldNumber}
        adminPassword={adminPassword}
        setAdminPassword={setAdminPassword}
        onCancel={handleModalClose}
        onConfirm={handleModalConfirm}
        projectId={currentProject?.project_id}
        useUidApi={false}
      />
    </div>
  );
};

export default D360LiveSlogStatus;
